import { createSlice, createAsyncThunk, nanoid } from '@reduxjs/toolkit'
import { apiGetNoteSessions } from 'services/NotesService'

export const fetchNoteTimelines = createAsyncThunk(
    'notes/apiGetNoteTimelines',
    async (params) => {
        const response = await apiGetNoteSessions(params)
        return response.data
    }
)

// Keep legacy name as alias so layout.js import still works during transition
export const fetchNoteSessions = fetchNoteTimelines

const createEntry = (text, authorId = null, authorName = 'You') => ({
    id: nanoid(),
    text,
    authorId,
    authorName,
    source: 'user',
    actionRef: null,
    createdAt: new Date().toISOString(),
    updatedAt: null,
})

const normalizeEntry = (entry) => {
    if (!entry || typeof entry !== 'object') {
        return createEntry('')
    }

    return {
        id: entry.id || nanoid(),
        text: typeof entry.text === 'string' ? entry.text : '',
        authorId: entry.authorId || null,
        authorName: entry.authorName || 'Unknown',
        source: entry.source || 'user',
        actionRef: entry.actionRef || null,
        createdAt: entry.createdAt || new Date().toISOString(),
        updatedAt: entry.updatedAt || null,
    }
}

const initialState = {
    timelines: {}, // ticketId -> { ticketId, ticketSubject, entries[], lastContributedAt }
    timelineOrder: [], // ticketId strings, most recent first
    currentTicketId: null,
}

const ensureTimeline = (state, ticketId, ticketSubject) => {
    const key = String(ticketId)
    if (!state.timelines[key]) {
        state.timelines[key] = {
            ticketId: key,
            ticketSubject: ticketSubject || `Ticket #${key}`,
            entries: [],
            lastContributedAt: null,
        }
        state.timelineOrder = [key, ...state.timelineOrder.filter((k) => k !== key)]
    }
    return state.timelines[key]
}

const notesSlice = createSlice({
    name: 'notes',
    initialState,
    reducers: {
        selectTimeline: (state, action) => {
            const { ticketId, ticketSubject } = action.payload || {}
            if (!ticketId) return
            ensureTimeline(state, ticketId, ticketSubject)
            state.currentTicketId = String(ticketId)
        },
        addEntry: (state, action) => {
            const { ticketId, entry: providedEntry, text, authorId, authorName } = action.payload
            const timeline = ensureTimeline(state, ticketId)
            const entry = providedEntry || createEntry(text, authorId, authorName)
            timeline.entries.push(normalizeEntry(entry))
            timeline.lastContributedAt = entry.createdAt
            const key = String(ticketId)
            state.timelineOrder = [
                key,
                ...state.timelineOrder.filter((k) => k !== key),
            ]
        },
        setTimelineEntries: (state, action) => {
            const { ticketId, entries = [] } = action.payload || {}
            if (!ticketId) return
            const timeline = ensureTimeline(state, ticketId)
            timeline.entries = entries.map((entry) => normalizeEntry(entry))
            const lastEntry = timeline.entries[timeline.entries.length - 1]
            timeline.lastContributedAt =
                lastEntry?.updatedAt || lastEntry?.createdAt || timeline.lastContributedAt
        },
        hydrateTimelines: (state, action) => {
            const { timelines: incoming = [] } = action.payload || {}
            const list = Array.isArray(incoming) ? incoming : []

            state.timelines = {}
            state.timelineOrder = []

            list.forEach((item) => {
                if (!item || !item.ticketId) return
                const key = String(item.ticketId)
                state.timelines[key] = {
                    ticketId: key,
                    ticketSubject: item.ticketSubject || `Ticket #${key}`,
                    entries: Array.isArray(item.entries)
                        ? item.entries.map((e) => normalizeEntry(e))
                        : [],
                    lastContributedAt: item.lastContributedAt || null,
                }
                state.timelineOrder.push(key)
            })

            if (state.currentTicketId && state.timelines[state.currentTicketId]) {
                return
            }
            state.currentTicketId = state.timelineOrder[0] || null
        },
        updateEntry: (state, action) => {
            const { ticketId, entryId, text } = action.payload
            const timeline = ensureTimeline(state, ticketId)
            const entry = timeline.entries.find((item) => item.id === entryId)
            if (entry) {
                entry.text = text
                entry.updatedAt = new Date().toISOString()
            }
        },
        removeEntry: (state, action) => {
            const { ticketId, entryId } = action.payload
            const timeline = ensureTimeline(state, ticketId)
            timeline.entries = timeline.entries.filter((item) => item.id !== entryId)
        },
    },
    extraReducers: {
        [fetchNoteTimelines.fulfilled]: (state, action) => {
            const { timelines: incoming = [] } = action.payload?.data || {}
            const list = Array.isArray(incoming) ? incoming : []

            state.timelines = {}
            state.timelineOrder = []

            list
                .sort((a, b) => new Date(b.lastContributedAt || 0) - new Date(a.lastContributedAt || 0))
                .forEach((item) => {
                    if (!item || !item.ticketId) return
                    const key = String(item.ticketId)
                    state.timelines[key] = {
                        ticketId: key,
                        ticketSubject: item.ticketSubject || `Ticket #${key}`,
                        entries: [],
                        lastContributedAt: item.lastContributedAt || null,
                    }
                    state.timelineOrder.push(key)
                })
        },
    },
})

export const {
    selectTimeline,
    addEntry,
    updateEntry,
    removeEntry,
    setTimelineEntries,
    hydrateTimelines,
} = notesSlice.actions

export default notesSlice.reducer
